<template>
  <q-dialog v-model="showDialog" persistent maximized>
    <q-card class="template-editor">
      <!-- Header -->
      <q-card-section class="row items-center q-pa-md bg-primary text-white">
        <div class="text-h5">Edit Template: {{ template.name }}</div>
        <q-space />
        <div class="text-caption q-mr-md">
          Used by {{ instanceCount }} equipment items
        </div>
        <!-- Action Buttons in Header -->
        <q-btn 
          label="Save" 
          icon="save"
          color="positive" 
          :loading="saving"
          :disable="!isValid"
          unelevated
          class="q-mr-sm"
          @click="saveTemplate"
        />
        <q-btn
          icon="close"
          aria-label="Close template editor"
          flat
          round
          dense
          @click="handleCancel"
        />
      </q-card-section>

      <!-- Main Content -->
      <q-card-section class="row no-wrap" style="height: calc(100vh - 140px)">
        <!-- Left Panel - Template Properties -->
        <div class="col-4 q-pr-md">
          <q-scroll-area style="height: 100%">
            <div class="text-h6 q-mb-md">Template Properties</div>
            
            <!-- Basic Properties -->
            <q-input
              v-model="template.name"
              label="Template Name"
              outlined
              class="q-mb-md"
              :rules="[val => !!val || 'Name is required']"
            />
            
            <q-input
              v-model="template.description"
              label="Description"
              outlined
              type="textarea"
              rows="3"
              class="q-mb-md"
            />

            <!-- Controller specific fields -->
            <div v-if="templateType === 'controller'" class="row q-col-gutter-sm q-mb-md">
              <div class="col-12 col-md-6">
                <q-input
                  v-model="template.manufacturer"
                  label="Manufacturer"
                  outlined
                  :rules="[val => !!val || 'Manufacturer is required']"
                />
              </div>
              <div class="col-12 col-md-6">
                <q-input
                  v-model="template.model_number"
                  label="Model Number"
                  outlined
                  :rules="[val => !!val || 'Model number is required']"
                />
              </div>
            </div>

            <!-- Device specific fields - Device Part Selection -->
            <div v-if="templateType === 'device'" class="q-mb-md">
              <q-select
                v-model="template.device_part_id"
                :options="devicePartOptions"
                label="Device Part (Optional)"
                outlined
                clearable
                option-value="id"
                option-label="display_name"
                emit-value
                map-options
                hint="Link to a device part for manufacturer/model info, or leave blank for generic template"
                @update:model-value="handleDevicePartChange"
              >
                <template #no-option>
                  <q-item>
                    <q-item-section class="text-grey">
                      No device parts found
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>

              <!-- Show manufacturer/model read-only when device part is selected -->
              <div v-if="selectedDevicePart" class="q-mt-sm row q-col-gutter-sm">
                <div class="col-6">
                  <q-input
                    :model-value="selectedDevicePart.manufacturer"
                    label="Manufacturer"
                    outlined
                    readonly
                    dense
                    hint="From device part"
                  />
                </div>
                <div class="col-6">
                  <q-input
                    :model-value="selectedDevicePart.model"
                    label="Model"
                    outlined
                    readonly
                    dense
                    hint="From device part"
                  />
                </div>
              </div>

              <!-- Device BRICK Class (for device templates only) -->
              <q-select
                v-model="template.brick_class"
                :options="deviceBrickClasses"
                label="BRICK Class"
                outlined
                required
                emit-value
                map-options
                option-value="value"
                option-label="label"
                class="q-mt-md"
                hint="Select the BRICK ontology class for this device type"
                :rules="[val => !!val || 'BRICK class is required']"
              />
            </div>

            <!-- Controller I/O Capacity -->
            <div v-if="templateType === 'controller'" class="q-mb-md">
              <div class="text-subtitle2 q-mb-sm">I/O Capacity</div>
              <div class="row q-col-gutter-sm">
                <div class="col-6">
                  <q-input
                    v-model.number="template.io_capacity.ai"
                    label="Analog Inputs"
                    outlined
                    type="number"
                    min="0"
                    dense
                  />
                </div>
                <div class="col-6">
                  <q-input
                    v-model.number="template.io_capacity.ao"
                    label="Analog Outputs"
                    outlined
                    type="number"
                    min="0"
                    dense
                  />
                </div>
                <div class="col-6">
                  <q-input
                    v-model.number="template.io_capacity.bi"
                    label="Binary Inputs"
                    outlined
                    type="number"
                    min="0"
                    dense
                  />
                </div>
                <div class="col-6">
                  <q-input
                    v-model.number="template.io_capacity.bo"
                    label="Binary Outputs"
                    outlined
                    type="number"
                    min="0"
                    dense
                  />
                </div>
              </div>
            </div>

            <!-- Equipment BRICK Class -->
            <q-select
              v-if="templateType === 'equipment'"
              v-model="template.brick_class"
              :options="equipmentBrickClasses"
              label="BRICK Class"
              outlined
              required
              emit-value
              map-options
              option-value="value"
              option-label="label"
              class="q-mb-md"
              hint="Select the BRICK ontology class for this equipment template"
            />

            <!-- Compound: BRICK classes are auto-derived from components -->
            <div v-if="templateType === 'compound' && template.template_config?.components?.length > 0" class="q-mb-md">
              <div class="text-caption text-grey-7 q-mb-xs">BRICK Classes (auto-derived from components)</div>
              <div class="row q-gutter-xs">
                <q-badge
                  v-for="bc in compoundBrickClasses"
                  :key="bc"
                  color="purple"
                  outline
                >
                  {{ formatBrickClass(bc) }}
                </q-badge>
              </div>
            </div>

            <!-- Labour Hours Section (equipment templates only) -->
            <div v-if="templateType === 'equipment'" class="q-mt-lg">
              <q-expansion-item
                icon="schedule"
                label="Labour Hours"
                caption="Standard hours per labour category"
                header-class="bg-grey-1 rounded-borders"
              >
                <q-card flat>
                  <q-card-section>
                    <div v-if="labourCategories.length === 0" class="text-grey text-center q-pa-md">
                      Loading categories...
                    </div>
                    <div v-else>
                      <div
                        v-for="category in labourCategories"
                        :key="category.slug"
                        class="row q-col-gutter-sm q-mb-sm items-center"
                      >
                        <div class="col-5">
                          <span class="text-body2">{{ category.name }}</span>
                        </div>
                        <div class="col-4">
                          <q-input
                            :model-value="getLabourHours(category.slug)"
                            type="number"
                            label="Hrs/unit"
                            outlined
                            dense
                            :min="0"
                            :step="0.25"
                            @update:model-value="(val) => setLabourHours(category.slug, 'hours_per_unit', val)"
                          />
                        </div>
                        <div class="col-3">
                          <q-input
                            :model-value="getLabourRate(category.slug)"
                            type="number"
                            label="$/hr"
                            outlined
                            dense
                            prefix="$"
                            :min="0"
                            @update:model-value="(val) => setLabourHours(category.slug, 'rate', val)"
                          />
                        </div>
                      </div>
                      <div class="text-caption text-grey q-mt-sm">
                        Set hours per unit for each labour category. These are used when generating labour from the quote BOM.
                      </div>
                    </div>
                  </q-card-section>
                </q-card>
              </q-expansion-item>
            </div>

            <!-- Template Usage Info -->
            <div class="q-mt-lg">
              <div class="text-h6 q-mb-md">Usage Information</div>
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-body2 q-mb-md">
                    <strong>{{ instanceCount }}</strong> {{ templateType === 'device' ? 'devices' : 'equipment items' }} use this template
                  </div>

                  <!-- Inline Instance List (only for equipment templates) -->
                  <div v-if="templateType === 'equipment' && instances && instances.length > 0" class="q-mt-md">
                    <div class="text-body2 text-weight-medium q-mb-sm">Equipment Using This Template:</div>
                    <q-scroll-area style="height: 200px; width: 100%">
                      <div v-for="(siteInstances, siteName) in instancesBySite" :key="siteName" class="q-mb-md">
                        <div class="text-caption text-weight-bold text-primary q-mb-xs">{{ siteName }}</div>
                        <q-list dense bordered separator class="rounded-borders">
                          <q-item v-for="instance in siteInstances" :key="instance.equipment_id" dense>
                            <q-item-section>
                              <q-item-label class="text-body2">{{ instance.equipment_name }}</q-item-label>
                              <q-item-label caption>{{ instance.location_name }} ({{ instance.location_type }})</q-item-label>
                            </q-item-section>
                            <q-item-section side>
                              <q-badge
                                :color="instance.status === 'installed' ? 'positive' : 'warning'"
                                text-color="white"
                                class="text-caption"
                              >
                                {{ instance.status }}
                              </q-badge>
                            </q-item-section>
                          </q-item>
                        </q-list>
                      </div>
                    </q-scroll-area>
                  </div>

                  <div v-else-if="instanceCount === 0" class="q-mt-md">
                    <div class="text-center q-pa-md bg-grey-2 rounded-borders">
                      <div class="text-body2 text-grey-8">No {{ templateType === 'device' ? 'devices' : 'equipment' }} use this template</div>
                      <div class="text-caption text-grey-6">This template can be safely deleted</div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </q-scroll-area>
        </div>

        <!-- Right Panel - Devices & Points Editor -->
        <div class="col-8">
          <q-scroll-area style="height: 100%">
            <!-- Devices Management (for equipment templates only) -->
            <div v-if="templateType === 'equipment'" class="q-mb-xl">
              <div class="row items-center q-mb-md">
                <div class="text-h6">Devices in Template ({{ template.default_devices.length }})</div>
                <q-space />
                <q-btn
                  icon="add"
                  label="Add Device"
                  color="primary"
                  size="sm"
                  @click="showAddDeviceDialog = true"
                />
              </div>

              <!-- Devices List -->
              <div v-if="template.default_devices && template.default_devices.length > 0">
                <q-expansion-item
                  v-for="(device, deviceIndex) in template.default_devices"
                  :key="`device-${deviceIndex}`"
                  :label="device.name"
                  :caption="`${formatBrickClass(device.brick_class)}${device.device_template_id ? ' • From Template' : ' • Custom Device'}`"
                  class="q-mb-sm"
                  bordered
                  default-opened
                  :header-class="device.device_template_id ? 'bg-orange-1' : 'bg-blue-1'"
                >
                  <template #header>
                    <q-item-section avatar>
                      <q-icon name="drag_indicator" class="cursor-move" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ device.name }}</q-item-label>
                      <q-item-label caption>
                        {{ formatBrickClass(device.brick_class) }}
                        <q-badge v-if="device.device_template_id" color="orange" class="q-ml-sm">
                          Template ID: {{ device.device_template_id }}
                        </q-badge>
                        <q-badge v-else color="blue" class="q-ml-sm">
                          Custom
                        </q-badge>
                      </q-item-label>
                    </q-item-section>
                    <q-item-section side>
                      <div class="row q-gutter-xs">
                        <q-btn
                          v-if="deviceIndex > 0"
                          icon="arrow_upward"
                          flat
                          round
                          dense
                          size="sm"
                          @click.stop="moveDeviceUp(deviceIndex)"
                        />
                        <q-btn
                          v-if="deviceIndex < template.default_devices.length - 1"
                          icon="arrow_downward"
                          flat
                          round
                          dense
                          size="sm"
                          @click.stop="moveDeviceDown(deviceIndex)"
                        />
                        <q-btn
                          icon="delete"
                          flat
                          round
                          dense
                          size="sm"
                          color="negative"
                          @click.stop="removeDevice(deviceIndex)"
                        />
                      </div>
                    </q-item-section>
                  </template>

                  <q-card flat bordered>
                    <q-card-section>
                      <!-- Device from Template - Read Only -->
                      <div v-if="device.device_template_id" class="q-pa-md bg-orange-1 rounded-borders">
                        <div class="text-subtitle2 q-mb-sm">
                          <q-icon name="info" color="orange" class="q-mr-xs" />
                          This device uses Device Template #{{ device.device_template_id }}
                        </div>
                        <div class="text-caption text-grey-7 q-mb-md">
                          Devices from templates are read-only. Edit the device template to change its structure.
                        </div>

                        <q-list dense bordered separator class="bg-white">
                          <q-item dense>
                            <q-item-section>
                              <q-item-label caption>BRICK Class</q-item-label>
                              <q-item-label>{{ formatBrickClass(device.brick_class) }}</q-item-label>
                            </q-item-section>
                          </q-item>
                          <q-item v-if="device.manufacturer" dense>
                            <q-item-section>
                              <q-item-label caption>Manufacturer</q-item-label>
                              <q-item-label>{{ device.manufacturer }}</q-item-label>
                            </q-item-section>
                          </q-item>
                          <q-item v-if="device.model_number" dense>
                            <q-item-section>
                              <q-item-label caption>Model</q-item-label>
                              <q-item-label>{{ device.model_number }}</q-item-label>
                            </q-item-section>
                          </q-item>
                        </q-list>

                        <!-- Device Points (Read Only) -->
                        <div v-if="device.points && device.points.length > 0" class="q-mt-md">
                          <div class="text-caption text-grey-7 q-mb-sm">Points ({{ device.points.length }})</div>
                          <q-list dense bordered separator class="bg-white">
                            <q-item v-for="(point, pointIndex) in device.points" :key="`device-${deviceIndex}-point-${pointIndex}`" dense>
                              <q-item-section>
                                <q-item-label>{{ point.name }}</q-item-label>
                                <q-item-label caption>
                                  <q-badge
                                    v-if="point.io_type"
                                    :color="getIoTypeColor(point.io_type)"
                                    text-color="white"
                                    class="q-mr-xs"
                                  >
                                    {{ point.io_type }}
                                  </q-badge>
                                  <q-badge
                                    v-if="point.value_type"
                                    :color="getValueTypeColor(point.value_type)"
                                    outline
                                    class="q-mr-xs"
                                  >
                                    {{ point.value_type }}
                                  </q-badge>
                                  <q-badge
                                    v-if="point.unit"
                                    color="teal"
                                    outline
                                    class="q-mr-xs"
                                  >
                                    {{ getUnitLabel(point.unit) }}
                                  </q-badge>
                                  {{ formatBrickClass(point.brick_class) }}
                                </q-item-label>
                              </q-item-section>
                            </q-item>
                          </q-list>
                        </div>
                      </div>

                      <!-- Custom Device - Fully Editable -->
                      <div v-else>
                        <div class="row q-col-gutter-sm q-mb-md">
                          <div class="col-12">
                            <q-input
                              v-model="device.name"
                              label="Device Name"
                              outlined
                              dense
                              :rules="[val => !!val || 'Name is required']"
                            />
                          </div>
                          <div class="col-12">
                            <q-select
                              v-model="device.brick_class"
                              :options="deviceBrickClasses"
                              label="BRICK Class"
                              outlined
                              dense
                              emit-value
                              map-options
                              option-value="value"
                              option-label="label"
                              :rules="[val => !!val || 'BRICK class is required']"
                            />
                          </div>
                          <div class="col-12">
                            <q-select
                              v-model="device.device_part_id"
                              :options="devicePartOptions"
                              option-value="id"
                              option-label="display_name"
                              label="Device Part (Optional)"
                              outlined
                              dense
                              clearable
                              emit-value
                              map-options
                              hint="Link to a device part for manufacturer/model info"
                            >
                              <template #no-option>
                                <q-item>
                                  <q-item-section class="text-grey">
                                    No device parts available
                                  </q-item-section>
                                </q-item>
                              </template>
                              <template #option="scope">
                                <q-item v-bind="scope.itemProps">
                                  <q-item-section>
                                    <q-item-label>{{ scope.opt.display_name }}</q-item-label>
                                    <q-item-label caption>{{ scope.opt.manufacturer }} - {{ scope.opt.model }}</q-item-label>
                                  </q-item-section>
                                </q-item>
                              </template>
                            </q-select>
                          </div>
                        </div>

                        <!-- Custom Device Points -->
                        <div class="q-mt-md">
                          <div class="row items-center q-mb-sm">
                            <div class="text-subtitle2">Device Points ({{ device.points?.length || 0 }})</div>
                            <q-space />
                            <q-btn
                              icon="add"
                              label="Add Point"
                              color="primary"
                              size="xs"
                              @click="addDevicePoint(deviceIndex)"
                            />
                          </div>

                          <q-list v-if="device.points && device.points.length > 0" bordered separator>
                            <q-item v-for="(point, pointIndex) in device.points" :key="`device-${deviceIndex}-point-${pointIndex}`">
                              <q-item-section>
                                <div class="row q-col-gutter-xs items-start no-wrap">
                                  <div class="col-2">
                                    <q-input
                                      v-model="point.name"
                                      label="Name"
                                      outlined
                                      dense
                                      :rules="[val => !!val || 'Required']"
                                    />
                                  </div>
                                  <div class="col-2">
                                    <q-select
                                      v-model="point.point_type"
                                      :options="pointTypeOptions"
                                      label="Point Type"
                                      outlined
                                      dense
                                      emit-value
                                      map-options
                                      option-value="value"
                                      option-label="label"
                                      :option-disable="opt => opt.disable"
                                      @update:model-value="(val) => handlePointTypeChange(point, val, `${deviceIndex}-${pointIndex}`)"
                                    >
                                      <template #option="scope">
                                        <q-item v-bind="scope.itemProps">
                                          <q-item-section v-if="scope.opt.category === true">
                                            <q-item-label class="text-weight-bold text-grey-8">
                                              {{ scope.opt.label }}
                                            </q-item-label>
                                          </q-item-section>
                                          <q-item-section v-else>
                                            <q-item-label>{{ scope.opt.label }}</q-item-label>
                                            <q-item-label caption>
                                              <q-badge
                                                :color="getIoTypeColor(scope.opt.ioType)"
                                                text-color="white"
                                                class="q-mr-xs"
                                              >
                                                {{ scope.opt.ioType }}
                                              </q-badge>
                                            </q-item-label>
                                          </q-item-section>
                                        </q-item>
                                      </template>
                                    </q-select>
                                  </div>
                                  <div class="col-1 text-center">
                                    <q-badge
                                      v-if="point.io_type"
                                      :color="getIoTypeColor(point.io_type)"
                                      text-color="white"
                                    >
                                      {{ point.io_type }}
                                    </q-badge>
                                  </div>
                                  <div class="col-1 text-center">
                                    <q-badge
                                      v-if="point.value_type"
                                      :color="getValueTypeColor(point.value_type)"
                                      outline
                                    >
                                      {{ point.value_type }}
                                    </q-badge>
                                  </div>
                                  <div class="col-3" style="min-width: 120px;">
                                    <PointUnitSelector
                                      v-model="point.unit_mapping_id"
                                      :quantity-kind="inferQuantityKind(point.brick_class)"
                                      label="QUDT Unit"
                                    />
                                  </div>
                                  <div class="col-2">
                                    <q-input
                                      v-model="point.brick_class"
                                      label="BRICK Class"
                                      outlined
                                      dense
                                      readonly
                                    />
                                  </div>
                                  <div class="col-1 flex items-center justify-center">
                                    <q-btn
                                      icon="delete"
                                      flat
                                      round
                                      dense
                                      size="sm"
                                      color="negative"
                                      @click="removeDevicePoint(deviceIndex, pointIndex)"
                                    />
                                  </div>
                                </div>
                              </q-item-section>
                            </q-item>
                          </q-list>
                          <div v-else class="text-center q-pa-md bg-grey-2 rounded-borders">
                            <div class="text-caption text-grey-7">No points configured</div>
                          </div>
                        </div>
                      </div>
                    </q-card-section>
                  </q-card>
                </q-expansion-item>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center q-pa-xl bg-grey-1 rounded-borders">
                <q-icon name="precision_manufacturing" size="64px" color="grey-5" />
                <div class="text-h6 text-grey-8 q-mt-md">No Devices Configured</div>
                <div class="text-body2 text-grey-6 q-mb-md">Add devices to define the {{ templateType === 'compound' ? 'compound' : 'equipment' }} template structure</div>
                <q-btn
                  icon="add"
                  label="Add First Device"
                  color="primary"
                  @click="showAddDeviceDialog = true"
                />
              </div>
            </div>

            <!-- Compound Template Components (Device Groupings) -->
            <div v-if="templateType === 'compound'" class="q-mb-xl">
              <div class="row items-center q-mb-md">
                <div class="text-h6">Component Devices ({{ (template.template_config?.components || []).length }})</div>
                <q-space />
                <q-btn
                  icon="add"
                  label="Add Component"
                  color="purple"
                  size="sm"
                  @click="handleAddComponent"
                />
              </div>

              <!-- Components List -->
              <div v-if="template.template_config?.components && template.template_config.components.length > 0">
                <q-list bordered separator class="q-mb-md">
                  <q-item v-for="(component, index) in template.template_config.components" :key="`component-${index}`">
                    <q-item-section avatar>
                      <q-icon
                        name="precision_manufacturing"
                        :color="component.role === 'parent' ? 'primary' : 'purple'"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>
                        {{ component.label || component.role }}
                        <q-badge v-if="component.role === 'parent'" color="primary" class="q-ml-sm">Parent</q-badge>
                      </q-item-label>
                      <q-item-label caption>
                        {{ formatBrickClass(component.brick_class) }}
                      </q-item-label>
                      <q-item-label v-if="component.default_points && component.default_points.length > 0" caption>
                        <q-icon name="scatter_plot" size="xs" class="q-mr-xs" />
                        {{ component.default_points.length }} default points
                      </q-item-label>
                    </q-item-section>
                    <q-item-section side>
                      <div class="row q-gutter-xs">
                        <q-btn
                          icon="edit"
                          flat
                          round
                          dense
                          size="sm"
                          @click="editComponent(index)"
                        />
                        <q-btn
                          v-if="component.role !== 'parent'"
                          icon="delete"
                          flat
                          round
                          dense
                          size="sm"
                          color="negative"
                          @click="removeComponent(index)"
                        />
                      </div>
                    </q-item-section>
                  </q-item>
                </q-list>

                <!-- Relationships between components -->
                <div v-if="template.template_config?.relationships && template.template_config.relationships.length > 0">
                  <div class="text-subtitle2 q-mb-sm">Relationships</div>
                  <q-list bordered separator>
                    <q-item v-for="(rel, index) in template.template_config.relationships" :key="`rel-${index}`">
                      <q-item-section avatar>
                        <q-icon name="link" color="info" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>
                          {{ getComponentLabel(rel.from) }}
                          <q-icon name="arrow_forward" size="xs" class="q-mx-xs" />
                          {{ rel.type }}
                          <q-icon name="arrow_forward" size="xs" class="q-mx-xs" />
                          {{ getComponentLabel(rel.to) }}
                        </q-item-label>
                        <q-item-label v-if="rel.metadata && Object.keys(rel.metadata).length > 0" caption>
                          Metadata: {{ JSON.stringify(rel.metadata) }}
                        </q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <q-btn
                          icon="delete"
                          flat
                          round
                          dense
                          size="sm"
                          color="negative"
                          @click="removeRelationship(index)"
                        />
                      </q-item-section>
                    </q-item>
                  </q-list>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center q-pa-xl bg-grey-1 rounded-borders">
                <q-icon name="dashboard_customize" size="64px" color="grey-5" />
                <div class="text-h6 q-mt-md text-grey-7">No Component Devices</div>
                <div class="text-body2 text-grey-6 q-mb-md">
                  Compound templates define groups of devices and their relationships (e.g., VSD + Motor, Valve + Actuator)
                </div>
                <q-btn
                  icon="add"
                  label="Add First Component"
                  color="purple"
                  @click="handleAddComponent"
                />
              </div>
            </div>

            <div class="text-h6 q-mb-md">Template-Level Points</div>

            <!-- Points List -->
            <div v-if="template.points && template.points.length > 0">
              <q-list bordered separator>
                <q-item
                  v-for="(point, index) in template.points"
                  :key="`point-${index}`"
                  class="point-item"
                >
                  <q-item-section>
                    <div class="row q-col-gutter-xs items-start no-wrap">
                      <!-- Point Name -->
                      <div class="col-2">
                        <q-input
                          v-model="point.name"
                          label="Name"
                          outlined
                          dense
                          :rules="[val => !!val || 'Required']"
                        />
                      </div>

                      <!-- Point Type (unified dropdown) -->
                      <div class="col-2">
                        <q-select
                          v-model="point.point_type"
                          :options="pointTypeOptions"
                          label="Point Type"
                          outlined
                          dense
                          emit-value
                          map-options
                          option-value="value"
                          option-label="label"
                          :option-disable="opt => opt.disable"
                          @update:model-value="(val) => handlePointTypeChange(point, val, index)"
                        >
                          <template #option="scope">
                            <q-item v-bind="scope.itemProps">
                              <q-item-section v-if="scope.opt.category === true">
                                <q-item-label class="text-weight-bold text-grey-8">
                                  {{ scope.opt.label }}
                                </q-item-label>
                              </q-item-section>
                              <q-item-section v-else>
                                <q-item-label>{{ scope.opt.label }}</q-item-label>
                                <q-item-label caption>
                                  <q-badge
                                    :color="getIoTypeColor(scope.opt.ioType)"
                                    text-color="white"
                                    class="q-mr-xs"
                                  >
                                    {{ scope.opt.ioType }}
                                  </q-badge>
                                  <q-badge
                                    :color="getValueTypeColor(scope.opt.valueType)"
                                    outline
                                  >
                                    {{ scope.opt.valueType }}
                                  </q-badge>
                                </q-item-label>
                              </q-item-section>
                            </q-item>
                          </template>
                        </q-select>
                      </div>

                      <!-- I/O Type Badge (read-only, auto-filled) -->
                      <div class="col-1 text-center">
                        <q-badge
                          v-if="point.io_type"
                          :color="getIoTypeColor(point.io_type)"
                          text-color="white"
                        >
                          {{ point.io_type }}
                        </q-badge>
                        <span v-else class="text-grey-5">-</span>
                      </div>

                      <!-- Value Type Badge (read-only, auto-filled) -->
                      <div class="col-1 text-center">
                        <q-badge
                          v-if="point.value_type"
                          :color="getValueTypeColor(point.value_type)"
                          outline
                        >
                          {{ point.value_type }}
                        </q-badge>
                        <span v-else class="text-grey-5">-</span>
                      </div>

                      <!-- QUDT Unit -->
                      <div class="col-3" style="min-width: 120px;">
                        <PointUnitSelector
                          v-model="point.unit_mapping_id"
                          :quantity-kind="inferQuantityKind(point.brick_class)"
                          label="QUDT Unit"
                        />
                      </div>

                      <!-- BRICK Class (read-only display) -->
                      <div class="col-3">
                        <q-input
                          v-model="point.brick_class"
                          label="BRICK Class"
                          outlined
                          dense
                          readonly
                        />
                      </div>

                      <!-- Actions -->
                      <div class="col-1 text-center">
                        <q-btn
                          icon="delete"
                          color="negative"
                          flat
                          round
                          dense
                          @click="removePoint(index)"
                        />
                      </div>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center q-pa-lg">
              <q-icon name="electrical_services" size="64px" color="grey-5" />
              <div class="text-h6 text-grey-8 q-mt-md">No Points Configured</div>
              <div class="text-body2 text-grey-6">Add points to define the template structure</div>
            </div>
            
            <!-- Add Point Button -->
            <div class="q-mt-md">
              <q-btn 
                icon="add" 
                label="Add Point" 
                color="primary" 
                @click="addPoint"
              />
            </div>
          </q-scroll-area>
        </div>
      </q-card-section>

      <!-- Footer Actions - Secondary Actions Only -->
      <q-card-actions class="bg-grey-1 q-pa-md justify-center">
        <q-btn 
          label="Delete Template" 
          flat 
          color="negative" 
          :disable="instanceCount > 0"
          icon="delete"
          @click="confirmDelete"
        />
      </q-card-actions>
    </q-card>


    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteConfirm" persistent>
      <q-card>
        <q-card-section>
          <div class="text-h6">Delete Template</div>
        </q-card-section>

        <q-card-section>
          Are you sure you want to delete the template "{{ template.name }}"?
          This action cannot be undone.
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="showDeleteConfirm = false" />
          <q-btn flat label="Delete" color="negative" :loading="deleting" @click="deleteTemplate" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Component Form Dialog (for compound templates) -->
    <ComponentFormDialog
      v-model="showComponentDialog"
      :component="editingComponent"
      :existing-components="template.template_config?.components || []"
      @save="handleComponentSave"
    />

    <!-- Add Device Dialog -->
    <q-dialog v-model="showAddDeviceDialog" persistent>
      <q-card style="min-width: 600px">
        <q-card-section>
          <div class="text-h6">Add Device to Template</div>
        </q-card-section>

        <q-card-section>
          <q-tabs v-model="addDeviceTab" class="q-mb-md">
            <q-tab name="template" label="From Device Template" />
            <q-tab name="custom" label="Custom Device" />
          </q-tabs>

          <q-tab-panels v-model="addDeviceTab">
            <!-- From Device Template -->
            <q-tab-panel name="template">
              <q-select
                v-model="selectedDeviceTemplateId"
                :options="deviceTemplateOptions"
                label="Select Device Template"
                outlined
                option-value="id"
                option-label="name"
                emit-value
                map-options
                :loading="loadingDeviceTemplates"
              >
                <template #no-option>
                  <q-item>
                    <q-item-section class="text-grey">
                      No device templates available
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </q-tab-panel>

            <!-- Custom Device -->
            <q-tab-panel name="custom">
              <div class="q-gutter-md">
                <q-input
                  v-model="newCustomDevice.name"
                  label="Device Name"
                  outlined
                  :rules="[val => !!val || 'Name is required']"
                />
                <q-select
                  v-model="newCustomDevice.brick_class"
                  :options="deviceBrickClasses"
                  label="BRICK Class"
                  outlined
                  emit-value
                  map-options
                  option-value="value"
                  option-label="label"
                  :rules="[val => !!val || 'BRICK class is required']"
                />
                <q-select
                  v-model="newCustomDevice.device_part_id"
                  :options="devicePartOptions"
                  option-value="id"
                  option-label="display_name"
                  label="Device Part (Optional)"
                  outlined
                  clearable
                  emit-value
                  map-options
                  hint="Link to a device part for manufacturer/model info"
                >
                  <template #no-option>
                    <q-item>
                      <q-item-section class="text-grey">
                        No device parts available
                      </q-item-section>
                    </q-item>
                  </template>
                  <template #option="scope">
                    <q-item v-bind="scope.itemProps">
                      <q-item-section>
                        <q-item-label>{{ scope.opt.display_name }}</q-item-label>
                        <q-item-label caption>{{ scope.opt.manufacturer }} - {{ scope.opt.model }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>
              </div>
            </q-tab-panel>
          </q-tab-panels>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="cancelAddDevice" />
          <q-btn
            flat
            label="Add Device"
            color="primary"
            :disable="!canAddDevice"
            @click="confirmAddDevice"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useTemplateStore } from '../../stores/templates';
  import { useQuasar } from 'quasar';
  import api from '../../services/api';
  import ComponentFormDialog from './ComponentFormDialog.vue';
  import PointUnitSelector from '../sites/PointUnitSelector.vue';
  import {
    POINT_TYPES,
    getGroupedPointTypeOptions,
    getPointTypeByName,
    getIoTypeColor,
    getIoTypeLabel,
    getValueTypeColor,
    getUnitLabel,
    UNIT_LABELS,
    IO_TYPES,
    VALUE_TYPES,
    isPhysicalIo,
  } from '@/config/pointTypes';
  import { formatBrickClass } from '@/composables/brick-schema/operations/useBrickValidation.js';

  export default {
    name: 'TemplateEditor',
    components: {
      ComponentFormDialog,
      PointUnitSelector,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      templateId: {
        type: Number,
        default: null,
      },
      templateType: {
        type: String,
        default: 'equipment', // Default to equipment for backward compatibility
        validator: (value) => ['equipment', 'device', 'controller', 'compound'].includes(value),
      },
      siteId: {
        type: Number,
        default: null, // If provided, filter instances to this site only
      },
    },
    emits: ['update:modelValue', 'template-updated', 'template-deleted'],
    setup(props, { emit }) {
      const $q = useQuasar();
      const templateStore = useTemplateStore();
    
      // Reactive data
      const showDialog = ref(false);

      // BUSINESS RULE: Templates define structure and default values only.
      // Airflow data (hvac_air_data) is ALWAYS instance-specific and NEVER stored in templates.
      // Airflow must be configured individually on each equipment instance to ensure accurate measurements.
      // The backend enforces this rule and returns HTTP 422 if templates include hvac_air_data.
      const template = ref({
        id: null,
        name: '',
        description: '',
        brick_class: '',
        points: [],
        default_points: [], // For equipment templates
        default_devices: [], // For equipment templates
        // Controller specific fields
        manufacturer: '',
        model_number: '',
        io_capacity: {
          ai: 0,
          ao: 0,
          bi: 0,
          bo: 0,
        },
        // Device specific fields
        device_part_id: null,
        // Compound specific fields
        template_config: { components: [], relationships: [] },
        // Labour hours per category (equipment templates only)
        labour_hours: {},
      // NOTE: No hvac_air_data field - airflow is instance-specific only
      });
      const instances = ref([]);
      const instanceCount = ref(0);
      const saving = ref(false);
      const deleting = ref(false);
      const showDeleteConfirm = ref(false);

      // Device parts for device templates
      const deviceParts = ref([]);
      const selectedDevicePart = ref(null);

      // Applicable units cache and loading state for points
      const applicableUnitsCache = ref({}); // Cache units by brick_class
      const loadingUnits = ref({}); // Track loading state per point index

      // Device management (for equipment templates)
      const showAddDeviceDialog = ref(false);
      const showAddComponentDialog = ref(false);
      const addDeviceTab = ref('template'); // 'template' or 'custom'

      // Component management (for compound templates)
      const showComponentDialog = ref(false);
      const editingComponent = ref(null);
      const editingComponentIndex = ref(-1);
      const selectedDeviceTemplateId = ref(null);
      const deviceTemplateOptions = ref([]);
      const loadingDeviceTemplates = ref(false);
      const newCustomDevice = ref({
        name: '',
        brick_class: '',
        device_part_id: null,
        points: [],
      });

      // Labour categories (for equipment templates)
      const labourCategories = ref([]);

      // Equipment BRICK class options
      const equipmentBrickClasses = [
        { label: 'Air Handling Unit', value: 'brick:Air_Handling_Unit' },
        { label: 'Variable Air Volume Box', value: 'brick:Variable_Air_Volume_Box' },
        { label: 'Fan Coil Unit', value: 'brick:Fan_Coil_Unit' },
        { label: 'Chiller', value: 'brick:Chiller' },
        { label: 'Boiler', value: 'brick:Boiler' },
        { label: 'Heat Pump', value: 'brick:Heat_Pump' },
        { label: 'Cooling Tower', value: 'brick:Cooling_Tower' },
        { label: 'Fan', value: 'brick:Fan' },
        { label: 'Pump', value: 'brick:Pump' },
        { label: 'Damper', value: 'brick:Damper' },
        { label: 'Valve', value: 'brick:Valve' },
        { label: 'Heat Exchanger', value: 'brick:Heat_Exchanger' },
        { label: 'Filter', value: 'brick:Filter' },
        { label: 'Switchgear', value: 'brick:Switchgear' },
        { label: 'Distribution Board', value: 'brick:Distribution_Board' },
        { label: 'Transformer', value: 'brick:Transformer' },
        { label: 'Generator', value: 'brick:Generator' },
        { label: 'UPS', value: 'brick:UPS' },
        { label: 'Battery', value: 'brick:Battery' },
        { label: 'Lighting Equipment', value: 'brick:Lighting_Equipment' },
        { label: 'Sensor', value: 'brick:Sensor' },
        { label: 'Controller', value: 'brick:Controller' },
        { label: 'Meter', value: 'brick:Meter' },
      ];
    
      // Use unified point types from config - grouped dropdown options
      const pointTypeOptions = computed(() => getGroupedPointTypeOptions());

      // Legacy point type mapping for backward compatibility with old data
      // Maps old-style point types (sensor, actuator, etc.) to nearest new type
      const legacyPointTypeMapping = {
        sensor: 'Temperature',
        actuator: 'Damper Command',
        setpoint: 'Temperature Setpoint',
        status: 'Running Status',
        command: 'Start/Stop',
        alarm: 'Alarm Status',
        other: 'Analog Input',
      };
    
      const brickClassesOptions = ref([
        'brick:Air_Handling_Unit',
        'brick:Variable_Air_Volume_Box',
        'brick:Chiller',
        'brick:Boiler',
        'brick:Fan',
        'brick:Pump',
        'brick:Temperature_Sensor',
        'brick:Pressure_Sensor',
        'brick:Flow_Sensor',
        'brick:Humidity_Sensor',
        'brick:Controller',
        'brick:Damper',
        'brick:Valve',
      ]);

      const brickClasses = ref([...brickClassesOptions.value]);

      // Device BRICK class options - grouped by type
      const deviceBrickClasses = [
        // Generic (for multi-sensor devices)
        { label: 'Sensor (Generic/Multi)', value: 'brick:Sensor' },
        // Sensors - Temperature
        { label: 'Temperature Sensor', value: 'brick:Temperature_Sensor' },
        { label: 'Air Temperature Sensor', value: 'brick:Air_Temperature_Sensor' },
        // Sensors - Humidity
        { label: 'Humidity Sensor', value: 'brick:Humidity_Sensor' },
        // Sensors - Pressure
        { label: 'Pressure Sensor', value: 'brick:Pressure_Sensor' },
        { label: 'Differential Pressure Sensor', value: 'brick:Differential_Pressure_Sensor' },
        { label: 'Air Differential Pressure Sensor', value: 'brick:Air_Differential_Pressure_Sensor' },
        // Sensors - Flow
        { label: 'Flow Sensor', value: 'brick:Flow_Sensor' },
        { label: 'Air Flow Sensor', value: 'brick:Air_Flow_Sensor' },
        { label: 'Water Flow Sensor', value: 'brick:Water_Flow_Sensor' },
        // Sensors - Air Quality
        { label: 'CO2 Sensor', value: 'brick:CO2_Sensor' },
        { label: 'Air Quality Sensor', value: 'brick:Air_Quality_Sensor' },
        // Sensors - Electrical
        { label: 'Current Sensor', value: 'brick:Current_Sensor' },
        { label: 'Voltage Sensor', value: 'brick:Voltage_Sensor' },
        { label: 'Power Sensor', value: 'brick:Power_Sensor' },
        { label: 'Energy Sensor', value: 'brick:Energy_Sensor' },
        // Sensors - Other
        { label: 'Occupancy Sensor', value: 'brick:Occupancy_Sensor' },
        { label: 'Luminance Sensor', value: 'brick:Luminance_Sensor' },
        // Actuators
        { label: 'Actuator', value: 'brick:Actuator' },
        { label: 'Damper', value: 'brick:Damper' },
        { label: 'Valve', value: 'brick:Valve' },
        // Equipment
        { label: 'Fan', value: 'brick:Fan' },
        { label: 'Pump', value: 'brick:Pump' },
        { label: 'Filter', value: 'brick:Filter' },
        { label: 'Heat Exchanger', value: 'brick:Heat_Exchanger' },
        { label: 'Variable Frequency Drive', value: 'brick:Variable_Frequency_Drive' },
        { label: 'Controller', value: 'brick:Controller' },
        { label: 'Meter', value: 'brick:Meter' },
      ];
    
      // Handle point type change - auto-populate all fields from config
      // Note: unit_mapping_id is NOT auto-set - user must select via PointUnitSelector
      const handlePointTypeChange = async (point, newType, pointIndex = null) => {
        const config = getPointTypeByName(newType);
        if (config) {
          point.io_type = config.io_type;
          point.value_type = config.value_type;
          point.brick_class = config.brick_class;
          // Reset unit_mapping_id when point type changes - user can select new unit
          point.unit_mapping_id = null;
        }
      };

      // Migrate legacy point type to new format
      const migrateLegacyPointType = (oldType) => {
        return legacyPointTypeMapping[oldType] || oldType;
      };

      // Check if a point type is a legacy value
      const isLegacyPointType = (pointType) => {
        return Object.keys(legacyPointTypeMapping).includes(pointType);
      };

      // Computed: unique BRICK classes from compound template components
      const compoundBrickClasses = computed(() => {
        const components = template.value.template_config?.components || [];
        return [...new Set(components.map((c) => c.brick_class).filter(Boolean))];
      });

      // Computed properties
      const isValid = computed(() => {
        // Basic validation - compound templates don't have a single brick_class
        if (!template.value.name) {
          return false;
        }
        if (props.templateType !== 'compound' && !template.value.brick_class) {
          return false;
        }

        // For device templates, check points array
        if (props.templateType === 'device' && template.value.points && template.value.points.length > 0) {
          return template.value.points.every((point) => point.name && point.point_type);
        }

        // For equipment templates, check default_points if present
        if (props.templateType === 'equipment' && template.value.default_points && template.value.default_points.length > 0) {
          return template.value.default_points.every((point) => point.name && point.point_type);
        }

        // If no points, still valid
        return true;
      });
    
      const instancesBySite = computed(() => {
        if (!instances.value || !Array.isArray(instances.value)) {return {};}

        const grouped = {};
        instances.value.forEach((instance) => {
          const siteName = instance.site_name || 'Unknown Site';
          if (!grouped[siteName]) {
            grouped[siteName] = [];
          }
          grouped[siteName].push(instance);
        });
        return grouped;
      });

      const canAddDevice = computed(() => {
        if (addDeviceTab.value === 'template') {
          return !!selectedDeviceTemplateId.value;
        } else {
          return !!(newCustomDevice.value.name && newCustomDevice.value.brick_class);
        }
      });

      const devicePartOptions = computed(() => {
        return deviceParts.value.map((part) => ({
          id: part.id,
          display_name: `${part.part_number} - ${part.manufacturer} ${part.model}`,
          manufacturer: part.manufacturer,
          model: part.model,
          brick_class: part.brick_class,
        }));
      });

      // Watch for dialog visibility changes
      watch(() => props.modelValue, (newVal) => {
        showDialog.value = newVal;
        if (newVal) {
          // Load device parts for device, equipment and compound templates
          if (props.templateType === 'device' || props.templateType === 'equipment' || props.templateType === 'compound') {
            loadDeviceParts();
          }
          // Load labour categories for equipment templates
          if (props.templateType === 'equipment') {
            fetchLabourCategories();
          }
          if (props.templateId) {
            loadTemplate();
          }
        }
      });

      watch(showDialog, (newVal) => {
        emit('update:modelValue', newVal);
      });

      // Watch for device_part_id changes to update selected device part
      watch(() => template.value.device_part_id, (newVal) => {
        if (newVal) {
          selectedDevicePart.value = deviceParts.value.find((part) => part.id === newVal) || null;
        } else {
          selectedDevicePart.value = null;
        }
      });

      // Methods
      const loadDeviceParts = async () => {
        try {
          const data = await api.get('/device-parts');
          deviceParts.value = Array.isArray(data) ? data : (data.data || []);
        } catch (error) {
          $q.notify({
            type: 'warning',
            message: 'Could not load device parts',
          });
        }
      };

      const handleDevicePartChange = (devicePartId) => {
        if (devicePartId) {
          selectedDevicePart.value = deviceParts.value.find((part) => part.id === devicePartId) || null;
        } else {
          selectedDevicePart.value = null;
        }
      };

      // Fetch applicable units for a BRICK class from the API
      const fetchApplicableUnits = async (brickClass) => {
        if (!brickClass) {return [];}
        if (applicableUnitsCache.value[brickClass]) {
          return applicableUnitsCache.value[brickClass];
        }

        try {
          const response = await api.get(`/brick/spec-fields/${encodeURIComponent(brickClass)}`);
          const units = response.applicable_units || [];
          // Create new object reference to trigger Vue reactivity for re-render
          applicableUnitsCache.value = { ...applicableUnitsCache.value, [brickClass]: units };
          return units;
        } catch (error) {
          console.error('Failed to fetch units for', brickClass, error);
          return [];
        }
      };

      // Get unit options for a point based on its brick_class
      const getUnitOptionsForPoint = (point) => {
        const units = applicableUnitsCache.value[point.brick_class] || [];
        if (units.length === 0) {
          // Fallback to default unit from point type config
          const defaultUnit = point.unit;
          return defaultUnit ? [{ label: getUnitLabel(defaultUnit), value: defaultUnit }] : [];
        }
        return units.map((u) => ({ label: getUnitLabel(u), value: u }));
      };

      // Infer quantity kind from BRICK class for unit filtering
      const inferQuantityKind = (brickClass) => {
        if (!brickClass) {return null;}
        const lower = brickClass.toLowerCase();

        if (lower.includes('temperature') || lower.includes('temp')) {return 'Temperature';}
        if (lower.includes('pressure')) {return 'Pressure';}
        if (lower.includes('flow') || lower.includes('airflow')) {return 'Flow';}
        if (lower.includes('power')) {return 'Power';}
        if (lower.includes('speed') || lower.includes('frequency')) {return 'Speed';}
        if (lower.includes('energy')) {return 'Energy';}
        if (lower.includes('humidity')) {return 'RelativeHumidity';}
        if (lower.includes('damper') || lower.includes('valve')) {return 'Dimensionless';}

        return null;
      };

      const loadTemplate = async () => {
        if (!props.templateId) {return;}

        try {
          const templateData = await templateStore.getTemplate(props.templateId, props.templateType);
          template.value = {
            ...templateData,
            // Ensure points array exists for backward compatibility
            points: templateData.points || templateData.default_points || [],
            default_points: templateData.default_points || [],
            default_devices: templateData.default_devices || [],
            labour_hours: templateData.labour_hours || {},
          };

          // Pre-fetch units for all existing points' brick classes
          const uniqueBrickClasses = [...new Set(
            template.value.points
              .map((p) => p.brick_class)
              .filter((bc) => bc),
          )];
          await Promise.all(uniqueBrickClasses.map((bc) => fetchApplicableUnits(bc)));

          // Load instances from API for both device and equipment templates
          const instanceData = await templateStore.getTemplateInstances(props.templateId, props.siteId, props.templateType);
          instances.value = instanceData.instances || [];
          instanceCount.value = instanceData.usage_count || 0;

          if (instances.value.length > 0) {
          }

        } catch (error) {
          $q.notify({
            type: 'negative',
            message: 'Failed to load template: ' + error.message,
          });
        }
      };
    
      const addPoint = async () => {
        // Use Temperature as default - most common point type
        const defaultConfig = getPointTypeByName('Temperature');
        const brickClass = defaultConfig?.brick_class || 'brick:Temperature_Sensor';

        // Pre-fetch units for the default brick class
        await fetchApplicableUnits(brickClass);

        template.value.points.push({
          name: '',
          point_type: 'Temperature',
          io_type: defaultConfig?.io_type || 'AI',
          value_type: defaultConfig?.value_type || 'analog',
          brick_class: brickClass,
          unit_mapping_id: null, // User selects via PointUnitSelector
        });
      };
    
      const removePoint = (index) => {
        template.value.points.splice(index, 1);
      };
    
      const filterBrickClasses = (val, update) => {
        update(() => {
          if (val === '') {
            brickClasses.value = [...brickClassesOptions.value];
          } else {
            const needle = val.toLowerCase();
            brickClasses.value = brickClassesOptions.value.filter(
              (v) => v.toLowerCase().indexOf(needle) > -1,
            );
          }
        });
      };
    
      const saveTemplate = async () => {
        if (!isValid.value) {
          const missingFields = [];
          if (!template.value.name) {missingFields.push('Name');}
          if (!template.value.brick_class) {missingFields.push('BRICK Class');}
          const invalidPoints = template.value.points?.filter((point) => !point.name || !point.point_type) || [];
          if (invalidPoints.length > 0) {missingFields.push(`${invalidPoints.length} points missing name/type`);}

          $q.notify({
            type: 'negative',
            message: `Missing required fields: ${missingFields.join(', ')}`,
            timeout: 8000,
          });
          return;
        }

        saving.value = true;

        try {
          let savedTemplate;
          const isNewTemplate = !template.value.id;

          if (isNewTemplate) {
            // Create new template
            savedTemplate = await templateStore.createTemplate(template.value, props.templateType);
          } else {
            // Update existing template
            savedTemplate = await templateStore.updateTemplate(template.value.id, template.value, props.templateType);
          }

          template.value = { ...savedTemplate };

          // Show appropriate success message
          let message = isNewTemplate ? 'Template created successfully' : 'Template updated successfully';
          if (savedTemplate.sync_message) {
            message = savedTemplate.sync_message;
          } else if (savedTemplate.instances_synchronized > 0) {
            message = `Template updated successfully. ${savedTemplate.instances_synchronized} equipment instances synchronized.`;
          }

          $q.notify({
            type: 'positive',
            message: message,
            timeout: 5000,
          });

          emit('template-updated', savedTemplate);
          showDialog.value = false;

        } catch (error) {

          $q.notify({
            type: 'negative',
            message: `Failed to save template: ${error.message}`,
          });

        } finally {
          saving.value = false;
        }
      };
    
      const handleCancel = () => {
        showDialog.value = false;
      };
    
      const confirmDelete = () => {
        if (instanceCount.value > 0) {
          $q.notify({
            type: 'negative',
            message: `Cannot delete template: it is used by ${instanceCount.value} equipment items`,
          });
          return;
        }
        showDeleteConfirm.value = true;
      };
    
      const deleteTemplate = async () => {
        deleting.value = true;

        try {
          await templateStore.deleteTemplate(template.value.id, props.templateType);

          $q.notify({
            type: 'positive',
            message: 'Template deleted successfully',
          });

          emit('template-deleted', template.value.id);
          showDeleteConfirm.value = false;
          showDialog.value = false;

        } catch (error) {
          $q.notify({
            type: 'negative',
            message: 'Failed to delete template: ' + error.message,
          });
        } finally {
          deleting.value = false;
        }
      };

      // Device management functions
      const loadDeviceTemplates = async () => {
        loadingDeviceTemplates.value = true;
        try {
          const data = await api.get('/v2/device-templates');
          deviceTemplateOptions.value = data.data || [];
        } catch (error) {
          $q.notify({
            type: 'warning',
            message: 'Could not load device templates',
          });
        } finally {
          loadingDeviceTemplates.value = false;
        }
      };

      const cancelAddDevice = () => {
        showAddDeviceDialog.value = false;
        selectedDeviceTemplateId.value = null;
        newCustomDevice.value = {
          name: '',
          brick_class: '',
          device_part_id: null,
          points: [],
        };
        addDeviceTab.value = 'template';
      };

      const confirmAddDevice = async () => {
        if (!template.value.default_devices) {
          template.value.default_devices = [];
        }

        if (addDeviceTab.value === 'template') {
          // Adding device from template
          const deviceTemplate = deviceTemplateOptions.value.find((t) => t.id === selectedDeviceTemplateId.value);
          if (deviceTemplate) {
            template.value.default_devices.push({
              name: deviceTemplate.name,
              brick_class: deviceTemplate.brick_class,
              manufacturer: deviceTemplate.manufacturer || '',
              model_number: deviceTemplate.model_number || '',
              device_template_id: deviceTemplate.id,
              points: deviceTemplate.points || [],
            });
          }
        } else {
          // Adding custom device
          template.value.default_devices.push({
            ...newCustomDevice.value,
            points: newCustomDevice.value.points || [],
          });
        }

        $q.notify({
          type: 'positive',
          message: 'Device added to template',
        });

        cancelAddDevice();
      };

      const removeDevice = (index) => {
        const device = template.value.default_devices[index];
        $q.dialog({
          title: 'Remove Device',
          message: `Remove "${device.name}" from template?`,
          cancel: true,
          persistent: true,
        }).onOk(() => {
          template.value.default_devices.splice(index, 1);
          $q.notify({
            type: 'positive',
            message: 'Device removed from template',
          });
        });
      };

      const moveDeviceUp = (index) => {
        if (index > 0) {
          const device = template.value.default_devices.splice(index, 1)[0];
          template.value.default_devices.splice(index - 1, 0, device);
        }
      };

      const moveDeviceDown = (index) => {
        if (index < template.value.default_devices.length - 1) {
          const device = template.value.default_devices.splice(index, 1)[0];
          template.value.default_devices.splice(index + 1, 0, device);
        }
      };

      const addDevicePoint = (deviceIndex) => {
        const device = template.value.default_devices[deviceIndex];
        if (!device.points) {
          device.points = [];
        }
        // Use Temperature as default - most common point type
        const defaultConfig = getPointTypeByName('Temperature');
        device.points.push({
          name: '',
          point_type: 'Temperature',
          io_type: defaultConfig?.io_type || 'AI',
          value_type: defaultConfig?.value_type || 'analog',
          brick_class: defaultConfig?.brick_class || 'brick:Temperature_Sensor',
          unit_mapping_id: null, // User selects via PointUnitSelector
        });
      };

      const removeDevicePoint = (deviceIndex, pointIndex) => {
        template.value.default_devices[deviceIndex].points.splice(pointIndex, 1);
      };

      // Watch for dialog opening to load device templates
      watch(() => showAddDeviceDialog.value, (newVal) => {
        if (newVal && (props.templateType === 'equipment' || props.templateType === 'compound')) {
          loadDeviceTemplates();
        }
      });

      // Compound template component management
      const getComponentLabel = (role) => {
        const component = template.value.template_config?.components?.find((c) => c.role === role);
        return component?.label || role;
      };

      const editComponent = (index) => {
        if (!template.value.template_config?.components) {return;}

        const component = template.value.template_config.components[index];
        editingComponent.value = { ...component };
        editingComponentIndex.value = index;
        showComponentDialog.value = true;
      };

      const handleComponentSave = (componentData) => {
        if (!template.value.template_config) {
          template.value.template_config = {
            components: [],
            relationships: [],
          };
        }

        if (editingComponentIndex.value >= 0) {
          // Editing existing component
          const oldRole = template.value.template_config.components[editingComponentIndex.value].role;

          // Update component
          template.value.template_config.components[editingComponentIndex.value] = componentData;

          // Update relationships if role changed (though it shouldn't in edit mode)
          if (oldRole !== componentData.role && template.value.template_config.relationships) {
            template.value.template_config.relationships.forEach((rel) => {
              if (rel.from === oldRole) {rel.from = componentData.role;}
              if (rel.to === oldRole) {rel.to = componentData.role;}
            });
          }

          $q.notify({
            type: 'positive',
            message: 'Component updated successfully',
          });
        } else {
          // Adding new component
          template.value.template_config.components.push(componentData);

          // Add relationships from the component data
          if (componentData.relationships?.length) {
            componentData.relationships.forEach((rel) => {
              const relationship = {
                from: rel.direction === 'to' ? rel.target : componentData.role,
                to: rel.direction === 'to' ? componentData.role : rel.target,
                type: rel.type,
              };

              if (!template.value.template_config.relationships) {
                template.value.template_config.relationships = [];
              }

              // Check if relationship doesn't already exist
              const exists = template.value.template_config.relationships.some(
                (r) => r.from === relationship.from && r.to === relationship.to && r.type === relationship.type,
              );

              if (!exists) {
                template.value.template_config.relationships.push(relationship);
              }
            });
          }

          $q.notify({
            type: 'positive',
            message: 'Component added successfully',
          });
        }

        // Reset editing state
        editingComponent.value = null;
        editingComponentIndex.value = -1;
        showComponentDialog.value = false;
      };

      const handleAddComponent = () => {
        editingComponent.value = null;
        editingComponentIndex.value = -1;
        showComponentDialog.value = true;
      };

      const removeComponent = (index) => {
        if (!template.value.template_config?.components) {return;}

        const component = template.value.template_config.components[index];
        if (component.role === 'parent') {
          $q.notify({
            type: 'negative',
            message: 'Cannot remove the parent component',
          });
          return;
        }

        $q.dialog({
          title: 'Remove Component',
          message: `Are you sure you want to remove "${component.label || component.role}"?`,
          cancel: true,
        }).onOk(() => {
          template.value.template_config.components.splice(index, 1);

          // Also remove any relationships involving this component
          if (template.value.template_config.relationships) {
            template.value.template_config.relationships = template.value.template_config.relationships.filter(
              (rel) => rel.from !== component.role && rel.to !== component.role,
            );
          }

          $q.notify({
            type: 'positive',
            message: 'Component removed',
          });
        });
      };

      const removeRelationship = (index) => {
        if (!template.value.template_config?.relationships) {return;}

        $q.dialog({
          title: 'Remove Relationship',
          message: 'Are you sure you want to remove this relationship?',
          cancel: true,
        }).onOk(() => {
          template.value.template_config.relationships.splice(index, 1);
          $q.notify({
            type: 'positive',
            message: 'Relationship removed',
          });
        });
      };

      // Labour hours helpers
      const fetchLabourCategories = async () => {
        try {
          const data = await api.get('/labour-categories/active');
          const categories = data.data || data || [];
          // Only show top-level categories (not sub-tasks)
          labourCategories.value = categories.filter((c) => !c.parent_id);
        } catch (error) {
          console.error('Failed to load labour categories:', error);
        }
      };

      const getLabourHours = (slug) => {
        return template.value.labour_hours?.[slug]?.hours_per_unit ?? 0;
      };

      const getLabourRate = (slug) => {
        return template.value.labour_hours?.[slug]?.rate ?? 100;
      };

      const setLabourHours = (slug, field, value) => {
        if (!template.value.labour_hours) {
          template.value.labour_hours = {};
        }
        if (!template.value.labour_hours[slug]) {
          template.value.labour_hours[slug] = { hours_per_unit: 0, rate: 100 };
        }
        template.value.labour_hours[slug][field] = parseFloat(value) || 0;

        // Clean up: remove entries with 0 hours to keep JSON clean
        if (field === 'hours_per_unit' && template.value.labour_hours[slug].hours_per_unit === 0) {
          delete template.value.labour_hours[slug];
        }
      };

      return {
        showDialog,
        template,
        instances,
        instanceCount,
        saving,
        deleting,
        showDeleteConfirm,
        // New unified point type system
        pointTypeOptions,
        handlePointTypeChange,
        getIoTypeColor,
        getIoTypeLabel,
        getValueTypeColor,
        getUnitLabel,
        getUnitOptionsForPoint,
        inferQuantityKind,
        applicableUnitsCache,
        loadingUnits,
        migrateLegacyPointType,
        isLegacyPointType,
        // Equipment and device BRICK classes
        equipmentBrickClasses,
        compoundBrickClasses,
        brickClasses,
        isValid,
        instancesBySite,
        devicePartOptions,
        selectedDevicePart,
        handleDevicePartChange,
        formatBrickClass,
        addPoint,
        removePoint,
        filterBrickClasses,
        saveTemplate,
        handleCancel,
        confirmDelete,
        deleteTemplate,
        // Device management
        showAddDeviceDialog,
        showAddComponentDialog,
        addDeviceTab,
        selectedDeviceTemplateId,
        deviceTemplateOptions,
        loadingDeviceTemplates,
        newCustomDevice,
        deviceBrickClasses,
        canAddDevice,
        cancelAddDevice,
        confirmAddDevice,
        removeDevice,
        moveDeviceUp,
        moveDeviceDown,
        addDevicePoint,
        removeDevicePoint,
        // Compound template methods
        getComponentLabel,
        editComponent,
        removeComponent,
        removeRelationship,
        handleAddComponent,
        handleComponentSave,
        showComponentDialog,
        editingComponent,
        editingComponentIndex,
        // Labour hours
        labourCategories,
        getLabourHours,
        getLabourRate,
        setLabourHours,
      };
    },
  };
</script>

<style scoped>
.template-editor {
  min-height: 100vh;
}

.point-item {
  padding: 16px;
}

.point-item:hover {
  background-color: #f5f5f5;
}
</style>